// src/components/Dashboard/RoleDashboard/RoleExtras/RoleVerification.jsx
import React, { useState } from 'react';
import { apiFetch } from "../../../../apiFetch";
import { useNavigate, useLocation } from 'react-router-dom';
import './RoleVerification.css';


const ROLE_CONFIG = {
    agent: {
        title: 'Agent Verification Request',
        idField: 'agentId',
        apiPath: '/api/request-role-verification', // 🔥 unified endpoint
        profilePath: (slug) => `/agent-dashboard/agent-profile/${slug}`,


        labels: {
            fullName: 'Full Legal Name:',
            licenseNumber: 'Football Agent License Number:',
            issuingBody: 'Issuing Body:',
            licenseFile: 'Upload Football Agent License:',
            idFile: 'Upload Government-Issued ID (e.g. Passport, National ID, or Driver’s License):',
            agencyName: 'Agency Name (Optional):',
            agencyWebsite: 'Agency Website (Optional):',
        },
        // requirements
        requireLicenseNumber: true,
        requireIssuingBody: true,
        requireLicenseFile: true,
        requireOrgName: false, // agencyName optional
        requireIdFile: true,
    },

    scout: {
        title: 'Scout Verification Request',
        idField: 'scoutId',
        apiPath: '/api/request-role-verification', // 🔥 unified endpoint
        profilePath: (slug) => `/scout-dashboard/scout-profile/${slug}`,
        labels: {
            fullName: 'Full Legal Name:',
            licenseNumber: 'Scouting Accreditation / Registration (if any):',
            issuingBody: 'Issuing Body / Organization (Optional):',
            licenseFile: 'Upload Scouting Accreditation / Reference Letter (Optional):',
            idFile: 'Upload Government-Issued ID (Required):',
            agencyName: 'Current Club / Organization (or Independent):',
            agencyWebsite: 'Portfolio / Website / Video Link (Optional):',
        },
        requireLicenseNumber: false,
        requireIssuingBody: false,
        requireLicenseFile: false,
        requireOrgName: true,  // current club / org / independent
        requireIdFile: true,
    },

    club: {
        title: 'Club Verification Request',
        idField: 'clubId',
        apiPath: '/api/request-role-verification', // 🔥 unified endpoint
        profilePath: (slug) => `/club-dashboard/club-profile/${slug}`,
        labels: {
            fullName: 'Contact Person Full Legal Name:',
            licenseNumber: 'Club Registration / Affiliation Number (Optional):',
            issuingBody: 'Football Association / League (Optional):',
            licenseFile: 'Upload Official Club Registration / League Proof:',
            idFile: 'Upload Government-Issued ID of Contact Person (Required):',
            agencyName: 'Club Legal Name:',
            agencyWebsite: 'Official Club Website or Main Social Page (Optional):',
        },
        requireLicenseNumber: false,
        requireIssuingBody: false,
        requireLicenseFile: true, // must upload some org proof
        requireOrgName: true,     // club legal name
        requireIdFile: true,
    },
};

const RoleVerification = ({ role: propRole }) => {
    const navigate = useNavigate();
    const location = useLocation();

    // Determine role: props > location.state.role > 'agent'
    const roleFromState = location.state?.role;
    const roleFromStorage =
        typeof window !== "undefined"
            ? localStorage.getItem("role") || localStorage.getItem("userRole")
            : null;

    const role = (propRole || roleFromState || roleFromStorage || "agent").toLowerCase();

    const config = ROLE_CONFIG[role] || ROLE_CONFIG.agent;

    // Determine id from state based on role
    const entityId =
        location.state?.[config.idField] ??
        location.state?.agentId ??
        location.state?.scoutId ??
        location.state?.clubId ??
        null;

    const profileSlug =
        location.state?.profileSlug ??
        location.state?.agentSlug ??
        location.state?.scoutSlug ??
        location.state?.clubSlug ??
        null;


    const fallbackSlug =
        localStorage.getItem(`${role}ProfileSlug`) || null;

    const effectiveProfileSlug = profileSlug || fallbackSlug;



    const [formData, setFormData] = useState({
        fullName: '',
        licenseNumber: '',
        issuingBody: '',
        licenseFile: null,
        idFile: null,
        agencyName: '',
        agencyWebsite: '',
        agreedToTerms: false,
    });

    const [message, setMessage] = useState('');

    if (!entityId || !effectiveProfileSlug) {
        return (
            <p>
                ❌ Profile information missing. Please return to your {role} profile and try again.
            </p>
        );
    }


    const handleChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        if (type === 'file') {
            setFormData((prev) => ({ ...prev, [name]: files[0] || null }));
        } else if (type === 'checkbox') {
            setFormData((prev) => ({ ...prev, [name]: checked }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.agreedToTerms) {
            setMessage("❌ Please agree to the platform’s terms and conditions.");
            return;
        }

        const data = new FormData();
        Object.entries(formData).forEach(([key, value]) => {
            data.append(key, value);
        });

        // unified payload
        data.append("role", role);
        data.append(config.idField, entityId);

        try {
            const path = config.apiPath;


            const res = await apiFetch(path, {
                method: "POST",
                body: data,
            });


            if (res.ok) {
                setMessage("✅ Your verification request has been submitted. Redirecting...");
                setTimeout(() => {
                    navigate(config.profilePath(effectiveProfileSlug), { replace: true });
                }, 1200);
            } else if (res.status === 401) {
                setMessage("⚠️ Your session expired. Please log in again.");
            } else {
                const text = await res.text().catch(() => "");
                setMessage(`❌ Failed to submit verification request. ${text || ""}`);
            }
        } catch (err) {
            console.error(err);
            setMessage("❌ Error submitting form.");
        }
    };


    const labels = config.labels;

    return (
        <div className="role-verification-form">
            <h2>{config.title}</h2>
            <form onSubmit={handleSubmit}>
                {/* Full name / contact person */}
                <label>{labels.fullName}</label>
                <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    placeholder={
                        role === 'club'
                            ? 'Contact full legal name'
                            : 'Full legal name'
                    }
                />

                {/* License / registration number (required only for agent) */}
                <label>{labels.licenseNumber}</label>
                <input
                    type="text"
                    name="licenseNumber"
                    value={formData.licenseNumber}
                    onChange={handleChange}
                    required={config.requireLicenseNumber}
                    placeholder={
                        role === 'agent'
                            ? 'e.g. FA-123456'
                            : 'Optional – leave blank if not applicable'
                    }
                />

                {/* Issuing body */}
                <label>{labels.issuingBody}</label>
                <input
                    type="text"
                    name="issuingBody"
                    value={formData.issuingBody}
                    onChange={handleChange}
                    required={config.requireIssuingBody}
                    placeholder={
                        role === 'agent'
                            ? 'e.g. FIFA, Swedish Football Association (SvFF)'
                            : 'Optional – e.g. national FA, league, organization'
                    }
                />

                {/* License / organization document upload */}
                <label>{labels.licenseFile}</label>
                <input
                    type="file"
                    name="licenseFile"
                    onChange={handleChange}
                    accept=".pdf,.jpg,.jpeg,.png"
                    required={config.requireLicenseFile}
                />

                {/* ID upload – always required by config */}
                <label>{labels.idFile}</label>
                <input
                    type="file"
                    name="idFile"
                    onChange={handleChange}
                    accept=".pdf,.jpg,.jpeg,.png"
                    required={config.requireIdFile}
                />

                {/* Organization / agency / club name */}
                <label>{labels.agencyName}</label>
                <input
                    type="text"
                    name="agencyName"
                    value={formData.agencyName}
                    onChange={handleChange}
                    required={config.requireOrgName}
                    placeholder={
                        role === 'agent'
                            ? 'e.g. Vision Sports Agency'
                            : role === 'scout'
                                ? 'e.g. FC Porto, Right to Dream, Independent Scout'
                                : 'e.g. Official club legal name'
                    }
                />

                {/* Website / portfolio / club site */}
                <label>{labels.agencyWebsite}</label>
                <input
                    type="text"
                    name="agencyWebsite"
                    value={formData.agencyWebsite}
                    onChange={handleChange}
                    placeholder={
                        role === 'agent'
                            ? 'https://example-agency.com'
                            : role === 'scout'
                                ? 'Portfolio or video link (optional)'
                                : 'Official club website or main social page (optional)'
                    }
                />

                <div className="checkbox-container">
                    <input
                        type="checkbox"
                        name="agreedToTerms"
                        checked={formData.agreedToTerms}
                        onChange={handleChange}
                        required
                        id="termsCheckbox"
                    />
                    <label
                        htmlFor="termsCheckbox"
                        style={{ marginLeft: '10px', color: 'white' }}
                    >
                        I confirm that the information I have provided is accurate and I
                        agree to the platform’s terms and conditions.
                    </label>
                </div>

                <button type="submit">Submit Verification Request</button>

                {message && <p className="status-message">{message}</p>}
            </form>
        </div>
    );
};

export default RoleVerification;

